import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import type { DirectusFolders } from '@/models/types';
import {
  readFolder,
  readFolders,
  createFolder as sdkCreateFolder,
  deleteFolder as sdkDeleteFolder,
  updateFolder as sdkUpdateFolder,
} from '@directus/sdk';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type Result<T> = QueryReturnValue<T, DirectusErrorType, {}>;

const getFoldersFn = async (
  query: RequestQuery<DirectusFolders> | undefined,
): Promise<Result<DirectusFolders[]>> => {
  try {
    const data = await directus.request<DirectusFolders[]>(readFolders(query as never));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const getFolderFn = async (id: string): Promise<Result<DirectusFolders>> => {
  try {
    const data = await directus.request<DirectusFolders>(readFolder(id));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const createFolderFn = async (body: Partial<DirectusFolders>): Promise<Result<DirectusFolders>> => {
  try {
    const data = await directus.request<DirectusFolders>(sdkCreateFolder(body as never));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const updateFolderFn = async ({
  id,
  body,
}: {
  id: string;
  body: Partial<DirectusFolders>;
}): Promise<Result<DirectusFolders>> => {
  try {
    const data = await directus.request<DirectusFolders>(sdkUpdateFolder(id, body as never));
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const deleteFolderFn = async (id: string): Promise<Result<void>> => {
  try {
    await directus.request<void>(sdkDeleteFolder(id));
    return { data: undefined };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const folderApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['folder', 'folders'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getFolders: builder.query<DirectusFolders[], RequestQuery<DirectusFolders> | undefined>({
        queryFn: getFoldersFn,
        providesTags: ['folders'],
      }),
      getFolder: builder.query<DirectusFolders, string>({
        queryFn: getFolderFn,
        providesTags: ['folder'],
      }),
      createFolder: builder.mutation<DirectusFolders, Partial<DirectusFolders>>({
        queryFn: createFolderFn,
        invalidatesTags: ['folders'],
      }),
      updateFolder: builder.mutation<
        DirectusFolders,
        { id: string; body: Partial<DirectusFolders> }
      >({
        queryFn: updateFolderFn,
        invalidatesTags: ['folder', 'folders'],
      }),
      deleteFolder: builder.mutation<void, string>({
        queryFn: deleteFolderFn,
        invalidatesTags: ['folder', 'folders'],
      }),
    }),
  });

export const {
  useGetFoldersQuery,
  useGetFolderQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} = folderApi;
